import { ActionIconButton, TableColumnHeader, TableColumnHeaderWithHoverCard } from "@/components/shared/table/Table";
import { PurchaseDetails } from "@/graphql/__generated__/graphql";
import { formatNumberLocale, convertBpToDecimal } from "@/utils/helpers";
import { ColumnDef } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { IntlShape } from "react-intl";
import { PencilIcon, TrashIcon, InfoIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

/** Simplified service type for name lookup */
interface ServiceLookup {
  id: number;
  name: string;
}

/**
 * Generates the column definitions for the Purchases table.
 * Simplified to show: Name & Description, Linked Services, Total Cost, and Actions.
 *
 * @param {IntlShape} intl - The internationalization object for formatting numbers and dates
 * @param {TFunction} t - The translation function
 * @param {boolean} userCanEdit - Whether the user has edit permissions
 * @param {(purchaseDetails: PurchaseDetails) => void} editActionFn - Handler function for the edit action
 * @param {(purchaseDetails: PurchaseDetails) => void} removeActionFn - Handler function for the remove action
 * @param {boolean} loading - Whether any action is currently loading
 * @param {ServiceLookup[]} services - Optional array of services to map service IDs to names
 * @returns {ColumnDef<PurchaseDetails, string>[]} Array of column definitions for the Purchases table
 */
export const getColumnsPurchases = (
  intl: IntlShape,
  t: TFunction,
  userCanEdit: boolean,
  editActionFn: (_purchaseDetails: PurchaseDetails) => void,
  removeActionFn: (_purchaseDetails: PurchaseDetails) => void,
  loading: boolean,
  services?: ServiceLookup[]
): ColumnDef<PurchaseDetails, string>[] => {
  /**
   * Helper to get service name by ID
   */
  const getServiceName = (serviceId: number): string => {
    const service = services?.find((s) => s.id === serviceId);
    return service ? service.name : `Service #${serviceId}`;
  };

  return [
    {
      id: "nameDescription",
      header: ({ column }) => (
        <TableColumnHeader
          hideSortingButton
          text={t("tables:purchases.columns.name")}
          column={column}
        />
      ),
      cell: ({ row }) => {
        const purchase = row.original.purchase;
        return (
          <HoverCard>
            <HoverCardTrigger asChild>
              <div className="max-w-[250px] cursor-pointer flex items-center gap-2">
                <InfoIcon className="h-4 w-4 text-muted-foreground" />
                <div className="font-medium">
                  {purchase.quantity} × {purchase.name}
                </div>
              </div>
            </HoverCardTrigger>
            <HoverCardContent side="right" className="w-80">
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">{t("tables:purchases.columns.details")}</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <span className="text-muted-foreground">{t("tables:purchases.columns.monthsDepreciation")}:</span>
                  <span>{purchase.monthsDepreciation} {t("tables:cars.columns.months")}</span>
                  <span className="text-muted-foreground">{t("tables:purchases.columns.quantity")}:</span>
                  <span>{purchase.quantity}</span>
                  <span className="text-muted-foreground">{t("tables:purchases.columns.yearlymaintenance")}:</span>
                  <span>{formatNumberLocale(intl, purchase.yearlyMaintenance, "currency")}</span>
                  <span className="text-muted-foreground">{t("tables:purchases.columns.yearlyrepair")}:</span>
                  <span>{formatNumberLocale(intl, purchase.yearlyRepair, "currency")}</span>
                  <span className="text-muted-foreground">{t("tables:purchases.columns.yearlyadditionals")}:</span>
                  <span>{formatNumberLocale(intl, purchase.yearlyAdditionals, "currency")}</span>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        );
      },
    },
    {
      id: "linkedServices",
      header: ({ column }) => (
        <TableColumnHeaderWithHoverCard
          hideSortingButton
          text={t("tables:purchases.columns.linkedservices")}
          column={column}
          hoverCardText={t("tables:purchases.tooltips.linkedservices")}
          icon={<InfoIcon className="h-4 w-4 text-muted-foreground" />}
        />
      ),
      cell: ({ row }) => {
        const links = row.original.links;
        if (!links || links.length === 0) {
          return (
            <span className="text-muted-foreground text-sm italic">
              {t("tables:purchases.columns.nolinkedservices")}
            </span>
          );
        }

        const visibleLinks = links.slice(0, 1);
        const remainingCount = links.length - 1;

        return (
          <div className="flex gap-1 max-w-[300px]">
            {visibleLinks.map((link) => (
              <Badge key={link.serviceId} variant="secondary" className="text-xs">
                <span className="text-muted-foreground max-w-[150px] truncate">{getServiceName(link.serviceId)}:</span>
                {convertBpToDecimal(link.ratio)}%
              </Badge>
            ))}
            {remainingCount > 0 && (
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Badge variant="outline" className="text-xs cursor-pointer">
                    +{remainingCount} {t("common:more")}
                  </Badge>
                </HoverCardTrigger>
                <HoverCardContent side="top" className="w-80">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">{t("tables:purchases.labels.alllinkedservices")}</h4>
                    <div className="flex flex-wrap gap-1">
                      {links.map((link) => (
                        <Badge key={link.serviceId} variant="secondary" className="text-xs">
                          <span className="max-w-[230px] truncate">{getServiceName(link.serviceId)}:</span>
                          {convertBpToDecimal(link.ratio)}%
                        </Badge>
                      ))}
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "purchase.purchaseCost",
      header: ({ column }) => (
        <TableColumnHeader
          hideSortingButton
          text={t("tables:purchases.columns.purchasecost")}
          column={column}
          align="end"
        />
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumberLocale(intl, row.original.purchase.purchaseCost, "currency")}
        </div>
      ),
    },
    {
      accessorKey: "purchase.margin",
      header: ({ column }) => (
        <TableColumnHeader
          hideSortingButton
          text={t("tables:purchases.columns.margin")}
          column={column}
          align="end"
        />
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {convertBpToDecimal(row.original.purchase.margin)}%
        </div>
      ),
    },
    {
      accessorKey: "purchase.revenue",
      header: ({ column }) => (
        <TableColumnHeader
          hideSortingButton
          text={t("tables:purchases.columns.revenue")}
          column={column}
          align="end"
        />
      ),
      cell: ({ row }) => (
        <div className="text-right font-medium">
          {formatNumberLocale(intl, row.original.purchase.revenue, "currency")}
        </div>
      ),
    },
    userCanEdit
      ? {
        id: "actions",
        size: 100,
        minSize: 100,
        maxSize: 100,
        cell: ({ row }) => (
          <div className="flex gap-2 justify-end">
            <ActionIconButton
              dataObj={row.original}
              fn={editActionFn}
              tooltipText={t("common:buttons.edit")}
              disabled={loading}
            >
              <PencilIcon className="h-4 w-4" />
            </ActionIconButton>
            <ActionIconButton
              dataObj={row.original}
              fn={removeActionFn}
              tooltipText={t("common:buttons.remove")}
              variant="destructive"
              disabled={loading}
            >
              <TrashIcon className="h-4 w-4" />
            </ActionIconButton>
          </div>
        ),
      }
      : null,
  ].filter(Boolean) as ColumnDef<PurchaseDetails, string>[];
};
